import type { ContentCardProps } from '../../types';
import type { APP_CONSTANTS } from '~/common/explicit-exports';
import { Card, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { IntegrationProviderIcon } from '~/features/integrations/components';
import { CardFooter } from '../CardFooter';
import { CardDescription, CardTitle } from '../CardText';
import { buildCardMetadata } from '../../utils';
import { StyledIconWrapper } from './ContentProviderCard.styles';

export interface ContentProviderCardProps extends ContentCardProps {
  onStart?: () => void;
  duration?: number | null;
  contentSource: (typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES)['UDEMY' | 'LINKEDIN_LEARNING'];
}

const { DEFAULT_COVER_IMAGES } = window.APP_CONSTANTS;

export const ContentProviderCard = ({
  contentSource,
  name,
  imageSrc,
  description,
  assignmentStatus,
  duration,
  onStart,
  tags = [],
  skills = [],
  overflowMenuItems = {},
  hideContentCTA = false,
  actions,
}: ContentProviderCardProps) => {
  const theme = useTheme();

  const START_NOW = I18n.t('js.learner.libraries.start_now');
  const CONTINUE = I18n.t('js.learner.libraries.continue');

  const metadata = buildCardMetadata(duration, skills, tags, theme.colors.on[600]);

  return (
    <Card onPress={onStart}>
      {imageSrc && (
        <Card.Hero
          src={imageSrc}
          errorImage={DEFAULT_COVER_IMAGES.SCORM}
          icon={
            <StyledIconWrapper>
              <IntegrationProviderIcon slug={contentSource} size="lg" />
            </StyledIconWrapper>
          }
        />
      )}
      <Card.Container data-testid="library-card">
        <Card.Content>
          <Card.Body>
            <Stack size={4}>
              {!imageSrc && (
                <StyledIconWrapper>
                  <IntegrationProviderIcon slug={contentSource} size="lg" />
                </StyledIconWrapper>
              )}
              <Stack size={2}>
                <CardTitle text={name} />
                {metadata.length ? <Card.Metadata metadata={metadata} /> : null}
                <CardDescription text={description} color={theme.colors.on[500]} />
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Content>
      </Card.Container>
      <CardFooter
        assignmentStatus={assignmentStatus}
        inProgressMessage={CONTINUE}
        startContentMessage={START_NOW}
        onStart={onStart}
        overflowMenuItems={overflowMenuItems}
        hideContentCTA={hideContentCTA}
        actions={actions}
      />
    </Card>
  );
};
